import { forwardRef, ReactNode } from 'react';
import { useLanguage } from '@/chassis/ui/i18n/useLanguage';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';

type I18nLabel = Record<Language, string>;

// ============================================
// List Types - Internal Types do Backend
// ============================================

// List (array of items with add/remove functionality)
interface ListProps<T = any> {
    value?: T[];
    onChange?: (value: T[]) => void;
    disabled?: boolean;
    className?: string;
    renderItem: (item: T, index: number, onUpdate: (value: T) => void, onRemove: () => void) => ReactNode;
    defaultItem: T;
    addButtonLabel?: string;
    i18nAddButtonLabel?: I18nLabel;
    maxItems?: number;
}

export const List = forwardRef<HTMLDivElement, ListProps>(({ value = [], onChange, disabled, className = '', renderItem, defaultItem, addButtonLabel = '+ Adicionar item', i18nAddButtonLabel, maxItems }, ref) => {
    const lang = useLanguage();
    const displayAddButtonLabel = i18nAddButtonLabel ? i18nAddButtonLabel[lang] : addButtonLabel;
    const handleAdd = () => {
        if (maxItems && value.length >= maxItems) return;
        onChange?.([...value, defaultItem]);
    };

    const handleRemove = (index: number) => {
        onChange?.(value.filter((_, i) => i !== index));
    };

    const handleUpdate = (index: number, updatedItem: any) => {
        const newValue = [...value];
        newValue[index] = updatedItem;
        onChange?.(newValue);
    };

    return (
        <div ref={ref} className={className}>
            <div className="space-y-3">
                {value.map((item, index) => (
                    <div key={index} className="relative p-4 border border-gray-300 rounded-md bg-gray-50">
                        {renderItem(
                            item,
                            index,
                            (updated) => handleUpdate(index, updated),
                            () => handleRemove(index)
                        )}
                    </div>
                ))}
            </div>
            {!disabled && (!maxItems || value.length < maxItems) && (
                <button type="button" onClick={handleAdd} className="mt-3 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 border border-blue-300 rounded-md hover:bg-blue-50">
                    {displayAddButtonLabel}
                </button>
            )}
            {maxItems && (
                <p className="mt-2 text-xs text-gray-500">
                    {value.length}/{maxItems} itens
                </p>
            )}
        </div>
    );
});
List.displayName = 'List';

// ListString (simple string list)
interface ListStringProps {
    value?: string[];
    onChange?: (value: string[]) => void;
    disabled?: boolean;
    className?: string;
    placeholder?: string;
    maxItems?: number;
}

export const ListString = forwardRef<HTMLDivElement, ListStringProps>(({ value = [], onChange, disabled, className = '', placeholder = 'Digite um valor', maxItems }, ref) => {
    return (
        <List
            ref={ref}
            value={value}
            onChange={onChange}
            disabled={disabled}
            className={className}
            defaultItem=""
            maxItems={maxItems}
            renderItem={(item, index, onUpdate, onRemove) => (
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={item}
                        onChange={(e) => onUpdate(e.target.value)}
                        disabled={disabled}
                        placeholder={placeholder}
                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                    {!disabled && (
                        <button type="button" onClick={onRemove} className="px-3 py-2 text-red-600 hover:text-red-800 border border-red-300 rounded-md">
                            ×
                        </button>
                    )}
                </div>
            )}
        />
    );
});
ListString.displayName = 'ListString';

// ListNumber (simple number list)
interface ListNumberProps {
    value?: number[];
    onChange?: (value: number[]) => void;
    disabled?: boolean;
    className?: string;
    placeholder?: string;
    maxItems?: number;
}

export const ListNumber = forwardRef<HTMLDivElement, ListNumberProps>(({ value = [], onChange, disabled, className = '', placeholder = 'Digite um número', maxItems }, ref) => {
    return (
        <List
            ref={ref}
            value={value}
            onChange={onChange}
            disabled={disabled}
            className={className}
            defaultItem={0}
            maxItems={maxItems}
            renderItem={(item, index, onUpdate, onRemove) => (
                <div className="flex gap-2">
                    <input
                        type="number"
                        value={item}
                        onChange={(e) => onUpdate(parseFloat(e.target.value) || 0)}
                        disabled={disabled}
                        placeholder={placeholder}
                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                    {!disabled && (
                        <button type="button" onClick={onRemove} className="px-3 py-2 text-red-600 hover:text-red-800 border border-red-300 rounded-md">
                            ×
                        </button>
                    )}
                </div>
            )}
        />
    );
});
ListNumber.displayName = 'ListNumber';
